import React, { useState } from "react"
import { Helmet } from "react-helmet-async"
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Link,
  Chip,
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import SiteWrapper from "../../home-pages/SiteWrapper"
import { lifetimeConverts } from "./convertData"

const stats = {
  generationsSearched: 8,
  ancestorsWithData: 420,
  adultConverts: 391,
  lifetimeConverts: 37,
  proxyBaptisms: 354,
}

const ConvertRow = ({ convert, index }) => {
  const [open, setOpen] = useState(false)
  const fsUrl = `https://www.familysearch.org/tree/person/details/${convert.pid}`

  return (
    <>
      <TableRow
        sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{convert.name}</TableCell>
        <TableCell>{convert.lifespan}</TableCell>
        <TableCell>{convert.baptized}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{convert.age}</TableCell>
        <TableCell sx={{ display: { xs: "none", md: "table-cell" }, color: "text.secondary", fontSize: "0.8125rem" }}>
          {convert.relationship}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, pl: { xs: 0, md: 6 } }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Relationship:</strong> {convert.relationship}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Baptized:</strong> {convert.baptized} (age {convert.age})
              </Typography>
              {convert.endowment && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Endowment:</strong> {convert.endowment}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>FamilySearch:</strong>{" "}
                <Link
                  href={fsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {convert.pid}
                </Link>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const StatCard = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      textAlign: "center",
      borderRadius: 2,
      flex: "1 1 140px",
    }}
  >
    <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e3a8a" }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
      {label}
    </Typography>
  </Paper>
)

const AncestorConverts = () => {
  return (
    <SiteWrapper>
      <Helmet>
        <title>Ancestor Converts | Dymock Family</title>
      </Helmet>

      <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
          {/* Page Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1e3a8a", m: 0 }}
            >
              Ancestor Converts
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "1.1rem", color: "#475569" }}>
              Adult converts in the Dymock ancestry
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "#94a3b8" }}>
              8 generations searched &mdash; Ryan Dymock, March 2026
            </Typography>
          </Box>

          {/* Stats */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mb: 4,
            }}
          >
            <StatCard label="Ancestors Searched" value={stats.ancestorsWithData} />
            <StatCard label="Adult Converts" value={stats.adultConverts} />
            <StatCard label="Lifetime Converts" value={stats.lifetimeConverts} />
            <StatCard label="Proxy Baptisms" value={stats.proxyBaptisms} />
          </Box>

          {/* Lifetime Converts Section */}
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Lifetime Converts
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary", lineHeight: 1.7 }}>
            These ancestors chose to join the Church during their own lifetime.
            They heard the gospel, gained a testimony, and were baptized. They
            are the ones who brought the covenant into your family lines.
          </Typography>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, mb: 4 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "#f8fafc", width: 40 }} />
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Lifespan</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Baptized</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Age</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", display: { xs: "none", md: "table-cell" } }}>
                    Relationship
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lifetimeConverts.map((convert, i) => (
                  <ConvertRow key={convert.pid} convert={convert} index={i} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Proxy note */}
          <Box
            sx={{
              borderLeft: "3px solid #1e3a8a",
              bgcolor: "#f8fafc",
              px: 2.5,
              py: 1.5,
              borderRadius: "0 8px 8px 0",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              An additional {stats.proxyBaptisms} ancestors were baptized by
              proxy in the temple after their death. They lived before or during
              the early Restoration and did not have the opportunity to hear the
              gospel in mortality.
            </Typography>
          </Box>
        </Box>
      </Box>
    </SiteWrapper>
  )
}

export default AncestorConverts
